import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const Calendar = ({ processos = [] }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Add previous month days if needed
  const firstDayOfWeek = monthStart.getDay();
  const prevMonthEnd = subMonths(monthStart, 1);
  const prevMonthDays = Array.from({ length: firstDayOfWeek }).map((_, i) => {
    const date = new Date(prevMonthEnd);
    date.setDate(date.getDate() - firstDayOfWeek + i + 1);
    return date;
  });

  // Add next month days if needed
  const totalCells = prevMonthDays.length + daysInMonth.length;
  const nextMonthDays = Array.from({ length: 42 - totalCells }).map((_, i) => {
    const date = new Date(monthStart);
    date.setMonth(date.getMonth() + 1);
    date.setDate(i + 1);
    return date;
  });

  const allDays = [...prevMonthDays, ...daysInMonth, ...nextMonthDays];

  const getEventosParaDia = (date) => {
    return processos.filter(p => {
      if (!p.data) return false;
      const processDate = new Date(p.data);
      return isSameDay(processDate, date);
    });
  };

  const handlePrevMonth = () => setCurrentDate(subMonths(currentDate, 1));
  const handleNextMonth = () => setCurrentDate(addMonths(currentDate, 1));

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-bold text-slate-800 flex items-center gap-2">
          <CalendarIcon size={20} className="text-blue-600" />
          Agenda de Processos
        </h3>
        <div className="flex gap-2">
          <button
            onClick={handlePrevMonth}
            className="p-2 hover:bg-slate-100 rounded-lg transition"
          >
            <ChevronLeft size={18} className="text-slate-600" />
          </button>
          <button
            onClick={handleNextMonth}
            className="p-2 hover:bg-slate-100 rounded-lg transition"
          >
            <ChevronRight size={18} className="text-slate-600" />
          </button>
        </div>
      </div>

      <div className="mb-6 text-center">
        <h4 className="font-bold text-lg text-slate-800">
          {format(currentDate, 'MMMM yyyy', { locale: ptBR })}
        </h4>
      </div>

      {/* Days of week header */}
      <div className="grid grid-cols-7 gap-2 mb-4">
        {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'].map(day => (
          <div key={day} className="text-center text-xs font-bold text-slate-500 py-2">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-2">
        {allDays.map((date, idx) => {
          const eventos = getEventosParaDia(date);
          const isCurrentMonth = isSameMonth(date, currentDate);
          const isSelected = isSameDay(date, selectedDate);
          const isToday = isSameDay(date, new Date());

          return (
            <div
              key={idx}
              onClick={() => setSelectedDate(date)}
              className={`
                min-h-24 p-2 rounded-lg border-2 cursor-pointer transition-all
                ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-blue-300'}
                ${!isCurrentMonth ? 'bg-slate-50' : 'bg-white'}
                ${isToday && isCurrentMonth ? 'ring-2 ring-blue-400' : ''}
              `}
            >
              <div className={`text-xs font-bold mb-1 ${isCurrentMonth ? 'text-slate-800' : 'text-slate-400'}`}>
                {format(date, 'd')}
              </div>
              <div className="space-y-1">
                {eventos.slice(0, 2).map((evento, i) => (
                  <div
                    key={i}
                    className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-semibold truncate"
                    title={`${evento.cliente} - ${evento.processo}`}
                  >
                    {evento.cliente} - {evento.processo}
                  </div>
                ))}
                {eventos.length > 2 && (
                  <div className="text-[10px] text-slate-500 px-1.5 font-bold">
                    +{eventos.length - 2} mais
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Events for selected date */}
      <div className="mt-8 pt-6 border-t border-slate-200">
        <h4 className="font-bold text-slate-800 mb-4">
          Processos - {format(selectedDate, "d 'de' MMMM", { locale: ptBR })}
        </h4>
        <div className="space-y-3">
          {getEventosParaDia(selectedDate).length > 0 ? (
            getEventosParaDia(selectedDate).map((p, idx) => (
              <div key={idx} className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-100">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="font-bold text-slate-800 text-sm">{p.processo}</p>
                    <p className="text-xs text-slate-600 mt-1">
                      <span className="font-semibold">Cliente:</span> {p.cliente}
                    </p>
                    <p className="text-xs text-slate-600">
                      <span className="font-semibold">Natureza:</span> {p.natureza}
                    </p>
                    <p className="text-xs text-slate-600">
                      <span className="font-semibold">Status:</span>{' '}
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          p.status === 'Ativo'
                            ? 'bg-emerald-100 text-emerald-700'
                            : p.status === 'Pendente'
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-slate-100 text-slate-700'
                        }`}
                      >
                        {p.status}
                      </span>
                    </p>
                    <p className="text-xs text-slate-600 mt-1 font-bold">
                      Valor: R$ {p.valor?.toLocaleString() || '0'}
                    </p>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <p className="text-slate-500 text-sm text-center py-6">
              Nenhum processo marcado para este dia
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Calendar;
